import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { tryLinkAccounts } from './link-accounts';

/** True when the hostname includes a tenant subdomain (e.g. theater.reservatie.app or theater.localhost). */
function hasSubdomain(): boolean {
  const h = window.location.hostname;
  return (
    h.endsWith('.localhost') ||
    (h.endsWith('.reservatie.app') && h !== 'reservatie.app')
  );
}

/**
 * Redirects users from top-level routes (/admin, /production) to tenant-scoped routes (/{slug}/admin)
 * by reading the first tenant from their JWT claims.
 *
 * When no tenants are found, attempts to link accounts (see ensureTenantAccessGuard).
 * Passes through when the tenant is already resolved via subdomain.
 */
export const autoTenantRedirectGuard: CanActivateFn = async () => {
  if (hasSubdomain()) {
    return true;
  }

  const auth = inject(AuthService);
  const router = inject(Router);
  const http = inject(HttpClient);

  try {
    const claims = await firstValueFrom(auth.idTokenClaims$);
    let tenants: string[] = claims?.['https://reservatie.app/tenants'] ?? [];

    if (tenants.length === 0) {
      try {
        tenants = await tryLinkAccounts(http, auth);
      } catch (err) {
        console.error('Account linking failed:', err);
      }
    }

    if (tenants.length === 0) {
      return router.createUrlTree(['/select-tenant']);
    }

    const firstTenant = tenants[0];
    const currentPath = router.url.split('?')[0];
    return router.createUrlTree([`/${firstTenant}${currentPath}`]);
  } catch (error) {
    console.error('Failed to read JWT claims for tenant redirect', error);
    return router.createUrlTree(['/public']);
  }
};
